import store from '@/store';
import * as api from '@/api';
import { IInvokeThirdPartyApiParams, IInvokeThirdPartyApiResponse } from '@/api/rg';
import { groupBy, topoSortDirected } from './tool';

/**
 * 新老值班切换.
 */
export async function switchOncall(params: { rgId: number; switchToNew: boolean }) {
    try {
        const res = await api.ctiApi.switchOncall(params);
        if (res && res.code === 200 && res.data) {
            store.commit('GET_RG_ONCALL_VERSION', params.switchToNew);
        }
    } catch (error) {
        console.error(error);
    }
}

/**
 * 查询rg组管理员.
 */
export async function fetchRgAdmins(rgId: number) {
    try {
        const res = await api.rgApi.getRgUser({ rgId, roles: 'RGADMIN' });
        const { code, data } = res;
        return code === 200 ? data.items : [];
    } catch (error) {
        return [];
    }
}

/**
 * 根据当前环境获取对应的tt-fe项目域名.
 */
export const getTtHost = () => {
    const { host } = window.location;
    if (host.indexOf('cti.cloud.test.sankuai.com') > -1) {
        return host.replace('cti', 'tt');
    }
    if (host.indexOf('localhost:3001') > -1) {
        return host.replace('3001', '3002');
    }
    if (host.indexOf('cti.fetc.st.sankuai.com') > -1) {
        return host.replace('cti', 'tt');
    }
    return 'tt.sankuai.com';
};

/**
 * 获取当前环境的tt-fe项目域名.
 */
export const getTtOrigin = () => {
    const { protocol } = window.location;
    const host = getTtHost();
    return `${protocol}//${host}`;
};

// #region 三方字段相关

type ThirdParams = IInvokeThirdPartyApiParams['params'];
type ThirdParamsValue = ThirdParams[keyof ThirdParams];
type ThirdResponseItem = IInvokeThirdPartyApiResponse['items'][number];

/**
 * !目前只有单多选下拉支持配置远程数据请求.
 */
type IThirdDropDownField = CommonTypes.IThirdSingleDropDownField | CommonTypes.IThirdMultiDropDownField;

/**
 * 以 tt-fe 项目为准.
 */
export class ThirdFieldUtils {
    /**
     * 用于存储字段id 与对应 label 的映射关系的静态缓存.
     * 这种做法并不推荐，但通过组件传递的方法层数较深且麻烦，故用全局变量强行处理.
     *
     * 单选下拉字段，value、label 为单个字符串.
     * 多选下拉字段，value、label 为字符串数组.
     */
    private static fieldIdToLabel = new Map<number, string | string[]>();

    /**
     * 自定义字段是否为三方字段.
     */
    static isThirdField(field: any): field is CommonTypes.ThirdField {
        return (
            this.isThirdSingleLineTextField(field) ||
            this.isThirdMultiLineTextField(field) ||
            this.isThirdSingleDropDownField(field) ||
            this.isThirdMultiDropDownField(field)
        );
    }

    /**
     * 自定义字段是否为三方单行文本.
     */
    static isThirdSingleLineTextField(field: any): field is CommonTypes.IThirdSingleLineTextField {
        return field?.inputType === 'THIRD_SINGLE_LINE_TEXT';
    }

    /**
     * 自定义字段是否为三方多行文本.
     */
    static isThirdMultiLineTextField(field: any): field is CommonTypes.IThirdMultiLineTextField {
        return field?.inputType === 'THIRD_MULTI_LINE_TEXT';
    }

    /**
     * 自定义字段是否为三方单选下拉.
     */
    static isThirdSingleDropDownField(field: any): field is CommonTypes.IThirdSingleDropDownField {
        return field?.inputType === 'THIRD_SINGLE_DROP_DOWN';
    }

    /**
     * 自定义字段是否为三方多选下拉.
     */
    static isThirdMultiDropDownField(field: any): field is CommonTypes.IThirdMultiDropDownField {
        return field?.inputType === 'THIRD_MULTI_DROP_DOWN';
    }

    /**
     * 获取三方字段的默认值.
     */
    static getDefaultValue(field: CommonTypes.ThirdField) {
        // 文本类型，直接用 defaultValue
        if (this.isThirdSingleLineTextField(field) || this.isThirdMultiLineTextField(field)) {
            return field.defaultValue || '';
        }

        // 下拉单选，默认值为 默认选项的 value 值.
        if (this.isThirdSingleDropDownField(field)) {
            const firstDefaultOption = (field.options || []).find(option => option.isDefault);
            return firstDefaultOption ? firstDefaultOption.value : '';
        }

        // 下拉多选，默认值为 默认选项的 value 值数组.
        if (this.isThirdMultiDropDownField(field)) {
            const defaultOptions = (field.options || []).filter(option => option.isDefault);
            return defaultOptions.map(option => option.value);
        }

        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
    }

    /**
     * 提取三方字段的依赖字段的三方id.
     */
    static resolveDependencies(field: CommonTypes.ThirdField): string[] {
        if (!(this.isThirdSingleDropDownField(field) || this.isThirdMultiDropDownField(field))) {
            return [];
        }
        const params = field.extraSettings?.requestParams;
        if (!params) return [];
        const res: string[] = [];
        Object.values(params).forEach((p: CommonTypes.ThirdParam) => {
            if (p.type === 'FORM') {
                res.push(p.value);
            }
        });
        return res;
    }

    /**
     * 当前三方字段是否有远程数据请求配置.
     */
    static hasRemoteSettings(field: CommonTypes.ThirdField): boolean {
        if (!(this.isThirdSingleDropDownField(field) || this.isThirdMultiDropDownField(field))) {
            return false;
        }
        return !!field.extraSettings?.url;
    }

    static cacheFieldLabel<T extends string | string[]>(fieldId: number, label: T) {
        if (!label) return;
        this.fieldIdToLabel.set(fieldId, label);
    }

    static retrieveFieldLabel<T extends string | string[]>(fieldId: number): T | undefined {
        return this.fieldIdToLabel.get(fieldId) as T | undefined;
    }

    /**
     * 校验`自定义模版中的三方字段配置`是否合法.
     *
     * @param contents 自定义字段配置.
     * @returns 校验结果.
     *
     * @example
     * ```ts
     * const contents = [
     *     { id: 1, inputType: 'THIRD_SINGLE_LINE_TEXT', name: '商户名称', defaultValue: '默认值' },
     *     { id: 2, inputType: 'THIRD_SINGLE_DROP_DOWN', name: '商户类型', defaultValue: '001', options: [{ label: '餐饮', value: '001' }] },
     *     { id: 3, inputType: 'THIRD_MULTI_DROP_DOWN', name: '商户标签', defaultValue: ['001'], options: [{ label: '美食', value: '001' }] },
     *     { id: 4, inputType: 'THIRD_MULTI_LINE_TEXT', name: '商户描述', defaultValue: '默认值' },
     * ];
     * const { ok, reason } = ThirdFieldUtils.validateCustomFieldContents(contents);
     * if (!ok) {
     *     console.error(reason);
     * }
     * ```
     */
    static validateCustomFieldContents(fields: CommonTypes.customField[]): {
        ok: boolean;
        reason: string;
    } {
        if (!Array.isArray(fields)) {
            return { ok: false, reason: '自定义字段配置必须是数组' };
        }

        const thirdFields = fields.filter(f => this.isThirdField(f)) as CommonTypes.ThirdField[];

        const hasDuplicateFieldId = () => {
            const fieldIds = thirdFields.map(f => f.thirdFieldId);
            return new Set(fieldIds).size !== fieldIds.length;
        };
        if (hasDuplicateFieldId()) {
            return { ok: false, reason: '存在重复的三方字段id' };
        }

        const groups = groupBy(thirdFields, f => f.ownerId);
        for (const fields of groups.values()) {
            const fieldDeps = fields.map(f => this.resolveDependencies(f));
            for (let i = 0; i < fields.length; i++) {
                const hasMissingDep = fieldDeps[i].some(dep => !fields.some(f => f.thirdFieldId === dep));
                if (hasMissingDep) {
                    return { ok: false, reason: `字段 ${fields[i].thirdFieldId} 的依赖字段不存在` };
                }
            }

            const { hasCycle } = topoSortDirected(
                fields.map(f => f.thirdFieldId),
                (_, index) => fieldDeps[index]
            );
            if (hasCycle) {
                return { ok: false, reason: '字段配置中存在循环依赖关系' };
            }
        }

        return { ok: true, reason: '' };
    }
}

// #endregion
